import React, { createContext, useContext, useRef, useCallback, useMemo, useEffect, useSyncExternalStore } from 'react';

// ── REFRESH REGISTRY ─────────────────────────────────────────────────────────
// Lets pull-to-refresh re-fetch a page's data instead of reloading the browser.
//
// A reload costs the whole bundle, every Firestore listener, the service
// worker's warm caches and the React tree — several seconds and a white flash —
// to update a like count. Pages register what to re-run; the gesture runs it.
//
// WHY A SET OF REFS, NOT A SET OF FUNCTIONS. A page's loader is redefined on
// every render (it closes over state), so registering the function itself would
// mean unregistering and re-registering constantly, and any refresh in flight
// would be holding a stale copy. The registry stores the REF instead: identity
// is stable for the component's whole life, and `.current` is always the newest
// version. Registration happens once, on mount.
//
// WHY IT DOES NOT RE-RENDER. Registering mutates a ref, never state — a page
// mounting must not re-render the provider and, through it, the entire app.

const Ctx = createContext({
  register: () => () => {},
  refreshAll: async () => false,
  subscribeBusy: () => () => {},
  isBusy: () => false,
});

export function RefreshProvider({ children }) {
  const handlers = useRef(new Set());

  // ── "A REFRESH IS RUNNING" ─────────────────────────────────────────────────
  // A SUBSCRIPTION, NOT STATE, for the same reason the registry is a ref: this
  // provider wraps the entire app, so putting a boolean in its state would
  // re-render every page each time a refresh starts and again when it ends.
  // Only the indicator cares, so only the indicator subscribes.
  const busy = useRef(false);
  const busyWatchers = useRef(new Set());

  const setBusy = useCallback((v) => {
    if (busy.current === v) return;
    busy.current = v;
    busyWatchers.current.forEach(fn => fn());
  }, []);

  const subscribeBusy = useCallback((fn) => {
    busyWatchers.current.add(fn);
    return () => { busyWatchers.current.delete(fn); };
  }, []);

  const isBusy = useCallback(() => busy.current, []);

  const register = useCallback((handlerRef) => {
    handlers.current.add(handlerRef);
    return () => { handlers.current.delete(handlerRef); };
  }, []);

  /**
   * Runs every registered loader. Resolves to false when nothing is registered,
   * which is the caller's signal to fall back to a real reload — a page that
   * has not been wired up must still refresh, just less gracefully.
   */
  const refreshAll = useCallback(async () => {
    const fns = [...handlers.current];
    if (!fns.length) return false;

    setBusy(true);
    try {
      // In PARALLEL, and allSettled: a page can register several independent
      // loaders (the profile has four), they do not depend on each other, and
      // one failing must not abandon the rest half-refreshed.
      await Promise.allSettled(fns.map((r) => {
        try { return r.current?.(); } catch (err) { return Promise.reject(err); }
      }));
    } finally {
      // `finally`, so a loader that throws cannot leave the indicator spinning
      // over a page that has already finished.
      setBusy(false);
    }
    return true;
  }, [setBusy]);

  const value = useMemo(
    () => ({ register, refreshAll, subscribeBusy, isBusy }),
    [register, refreshAll, subscribeBusy, isBusy],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Registers `fn` to be re-run when the user pulls to refresh.
 *
 * The callback may be redefined on every render — only the ref is registered,
 * so there is no dependency array to keep in sync and no stale-closure trap.
 *
 *   useRefreshable(() => loadAll());
 */
export function useRefreshable(fn) {
  const { register } = useContext(Ctx);
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => register(ref), [register]);
}

/** The imperative side: returns `refreshAll`. Used by the pull gesture. */
export function useRefresh() {
  return useContext(Ctx).refreshAll;
}

/**
 * True while a refresh is running, from ANY trigger.
 *
 * Lets one indicator serve both gestures: the pull, and tapping the tab you
 * are already on. Without it the tap refreshed silently — the feed changed
 * under you with nothing to say it was working, which reads as a dead button
 * followed by an unexplained jump.
 *
 * useSyncExternalStore rather than useState + useEffect: the value is read
 * during render, so there is no frame where the indicator has been told to
 * show but has not yet.
 */
export function useRefreshBusy() {
  const { subscribeBusy, isBusy } = useContext(Ctx);
  return useSyncExternalStore(subscribeBusy, isBusy, () => false);
}
